import { Banner } from '@/components/banner';
import { useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Form } from '@/components/form';
import { requiredState, zt } from '@/util/validators';
import {
  CreateEncounterErrorResponse,
  CreateEncounterErrorType,
  GetPersonalInfoResponse,
  GetReferredProviderErrorType,
} from '@/api/api.schemas';
import { useCreateEncounters } from '@/api/create-encounter';
import { Navigate, useNavigate } from '@/util/react-router-dom';
import { StateSelector } from '@/components/form-controls/state-selector';
import { Section } from '@/components/containers/section';
import { PageContainer } from '@/components/containers/page-container';
import { GradientFormTitle } from '@/components/containers/gradient-form-title';
import { VisitFormButtonRow } from '@/components/containers/visit-form-button-row';
import { Checkbox } from '@/components/inputs/checkbox';
import styles from './pre-screen-questions-page.module.css';
import clsx from 'clsx';
import { VisitNavigation } from '@/components/navigation/visit-navigation';
import { useMediaQuery } from '@/hooks/use-media-query';
import { useGetReferredProvider } from '@/api/get-referred-provider';
import { useGetEncountersAvailableStates } from '@/api/get-available-states';
import { ReferralStorage } from '@/services/referral-storage';
import { useEffect, useState } from 'react';
import { useBranding } from '@/outlets/branding-outlet';
import { VisitTitleButtons } from '@/components/navigation/visit-title-buttons';
import { useVisitNavigation } from '@/contexts/visit-navigation-context';
import { MarketingSpot } from '@/components/marketing-spot';
import { useGetPatient } from '@/api/get-personal-info';
import { useLoggedUser } from '@/contexts/current-user-context';
import { IdVerificationSection } from '@/components/encounter/pre-screen-questions/id-verification-section';
import { EncounterCreatedModal } from '@/components/encounter/pre-screen-questions/encounter-created-modal';
import { Loader } from '@/components/loaders/loader';
import { useReferenceData } from '@/contexts/reference-data-context';
import { MaterialSymbolIcon } from '@/components/material-symbol-link';
import { LogoutButton } from '@/components/logout-button';
import { useTranslation } from '@/contexts/i18n-context';

interface EncounterModalInfo {
  encounterId: number;
  price: number;
}

const translationBase = 'visit.preScreenQuestions';

const basePreScreenQuestionsSchema = z.object({
  medHistoryConsent: z.boolean().refine((value) => value, {
    message: zt(`${translationBase}.medHistoryConsent.error`),
  }),
});

const preScreenQuestionsWithStateSchema = basePreScreenQuestionsSchema.extend({
  receiveCareState: requiredState(`${translationBase}.state.error`),
});

type BasePreScreenQuestionsFormData = z.infer<typeof basePreScreenQuestionsSchema>;
type PreScreenQuestionsWithStateFormData = z.infer<typeof preScreenQuestionsWithStateSchema>;

type PreScreenQuestionsFormData =
  | BasePreScreenQuestionsFormData
  | PreScreenQuestionsWithStateFormData;

const referralStorage = new ReferralStorage();

export function PreScreenQuestionsPage() {
  const { user } = useLoggedUser();
  const { data: patient, isFetching } = useGetPatient(user.id);

  if (isFetching) {
    return <Loader type="large" />;
  }

  if (!patient) {
    return <Navigate to="/system-error" />;
  }

  return <PreScreenQuestionsForm patient={patient} />;
}

interface PreScreenQuestionsFormProps {
  patient: GetPersonalInfoResponse;
}

function PreScreenQuestionsForm({ patient }: PreScreenQuestionsFormProps) {
  const { t } = useTranslation();
  const { networkId, hasInsurance, medicaidFlowEnabled } = useBranding();
  const [encounterInfo, setEncounterInfo] = useState<EncounterModalInfo>();
  const navigate = useNavigate();
  const { nextStep } = useVisitNavigation();
  const useStatePreScreenQuestions =
    !(medicaidFlowEnabled && patient.isMedicaid) && !patient.encounterState;

  const schema = useStatePreScreenQuestions
    ? preScreenQuestionsWithStateSchema
    : basePreScreenQuestionsSchema;

  const form = useForm<PreScreenQuestionsFormData>({
    resolver: zodResolver(schema),
    mode: 'onBlur',
    defaultValues: useStatePreScreenQuestions
      ? {
          receiveCareState: patient?.state ?? undefined,
        }
      : undefined,
  });

  const { data: referredProvider, error: referredProviderError } = useGetReferredProvider(
    {
      referralId: referralStorage.referralId,
    },
    {
      query: {
        enabled: !!referralStorage.referralId,
      },
    },
  );

  const { data: availableStates, isFetching: isFetchingStates } = useGetEncountersAvailableStates({
    networkId,
  });

  const { mutate: createEncounter, isPending, error } = useCreateEncounters();
  const getErrorMessage = (error: CreateEncounterErrorResponse) => {
    if (error.type === CreateEncounterErrorType.EncounterAlreadyExists) {
      return t(`${translationBase}.errors.conflict`);
    }
    if (error.type === CreateEncounterErrorType.StateNotAvailable) {
      return t(`${translationBase}.errors.stateNotAvailable`);
    }
    return t('common.errors.unknown');
  };

  useEffect(() => {
    if (referredProviderError?.type === GetReferredProviderErrorType.NotFound) {
      referralStorage.remove();
    }
  }, [referredProviderError]);

  const onSubmit: SubmitHandler<PreScreenQuestionsFormData> = async (data) => {
    let receiveCareState;

    if ('receiveCareState' in data) {
      receiveCareState = data.receiveCareState;
    }

    createEncounter(
      {
        data: {
          receiveCareState,
          referredProviderId: referredProvider?.referredProviderId,
          networkId,
        },
      },
      {
        onSuccess: (data) => {
          referralStorage.remove();

          if (typeof data.price === 'number') {
            setEncounterInfo({ encounterId: data.encounterId, price: data.price });
            return;
          }

          const pathToNavigate = `/visit/${data.encounterId}/${hasInsurance ? 'insurance-information' : 'questions'}`;
          navigate(pathToNavigate);
        },
      },
    );
  };

  const isMobile = useMediaQuery('--screen-md');

  if (isFetchingStates) {
    return <Loader type="large" />;
  }

  return (
    <PageContainer>
      {!isMobile && <VisitTitleButtons hideSaveProgress />}
      <GradientFormTitle>
        {isMobile && <VisitTitleButtons hideSaveProgress />}
        <VisitNavigation />
        <h1 className="h3">{t(`${translationBase}.title`)}</h1>
        <p>{t(`${translationBase}.description`)}</p>
      </GradientFormTitle>
      <MarketingSpot page="preScreen" spot="top" className={styles.marketingContent} />
      <Form form={form} onSubmit={onSubmit}>
        <Section className={styles.form}>
          {!!error && <Banner type="error">{getErrorMessage(error)}</Banner>}
          {!!patient.encounterState && <ReadOnlyState state={patient.encounterState} />}
          {useStatePreScreenQuestions && (
            <div className={styles.formItem}>
              <label className="h4" htmlFor="receiveCareState">
                {t(`${translationBase}.state.label`)}
              </label>
              <span className={styles.helpText}>{t(`${translationBase}.state.help`)}</span>
              <StateSelector
                name="receiveCareState"
                id="receiveCareState"
                placeholder={t(`${translationBase}.state.placeholder`)}
                className={clsx(styles.input, styles.stateInput)}
                states={availableStates?.states}
              />
            </div>
          )}
          <div className={styles.formItem}>
            <span className="h4">{t(`${translationBase}.medHistoryConsent.title`)}</span>
            <span className={styles.helpText}>
              {t(`${translationBase}.medHistoryConsent.help`)}
            </span>
            <Checkbox
              className={styles.input}
              name="medHistoryConsent"
              testId="pre-screen-med-history-consent"
              label={t(`${translationBase}.medHistoryConsent.label`)}
            />
          </div>
        </Section>

        <IdVerificationSection patient={patient} />

        <MarketingSpot page="preScreen" spot="bottom" className={styles.marketingContent} />

        {!hasInsurance && encounterInfo && (
          <EncounterCreatedModal
            encounterId={encounterInfo.encounterId}
            price={encounterInfo.price}
            onClose={() => navigate(`/visit/${encounterInfo.encounterId}/questions`)}
          />
        )}

        <VisitFormButtonRow
          nextStep={nextStep?.name}
          rightIcon="arrow_forward"
          buttonTextKey="common.next"
          type="submit"
          loading={isPending}
        />
      </Form>
    </PageContainer>
  );
}

interface ReadOnlyStateProps {
  state: string;
}

function ReadOnlyState({ state }: ReadOnlyStateProps) {
  const { t } = useTranslation();
  const { getStateName } = useReferenceData();

  return (
    <div className={styles.formItem}>
      <label className="h4">{t(`${translationBase}.readonlyState.label`)}</label>
      <span className={styles.helpText}>{t(`${translationBase}.readonlyState.description`)}</span>
      <div className={styles.readonlyStateContainer}>
        <MaterialSymbolIcon name="location_on" />
        <div className={styles.stateDetails}>
          <span>{getStateName(state)}</span>
          <span className={clsx('body-3', 'semibold', styles.stateHelp)}>
            {t(`${translationBase}.readonlyState.help`, {
              logoutLink: (
                <LogoutButton
                  variant="link"
                  className={clsx('body-3', styles.logoutLink)}
                  key="logout-link"
                />
              ),
            })}
          </span>
        </div>
      </div>
    </div>
  );
}
